package com.gloopgo.game.network

import com.gloopgo.game.engine.EjectedMass
import com.gloopgo.game.engine.FoodPellet
import com.gloopgo.game.engine.GloopPlayer
import com.gloopgo.game.engine.LeaderboardEntry
import kotlinx.coroutines.flow.Flow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Compact serialized network DTO for a player in the shared world.
 * Note: Timestamps and colors use Double and String to ensure primitive JSON types
 * across Kotlin/JS (Web) and JVM/Android without { high, low } object nesting.
 */
@Serializable
data class PlayerNetworkState(
    @SerialName("id") val id: String = "",
    @SerialName("n") val name: String = "",
    @SerialName("c") val color: String = "4294967295",
    @SerialName("e") val emoji: String = "",
    @SerialName("cells") val cells: List<CellNetworkState> = emptyList(),
    @SerialName("a") val isAlive: Boolean = true,
    @SerialName("ts") val lastTimestamp: Double = 0.0,
    @SerialName("ja") val joinedAt: Double = 0.0
)

/**
 * Compact serialized network DTO for a single cell belonging to a player.
 */
@Serializable
data class CellNetworkState(
    @SerialName("id") val id: String = "",
    @SerialName("x") val x: Float = 0f,
    @SerialName("y") val y: Float = 0f,
    @SerialName("m") val mass: Float = 10f
)

/**
 * Compact serialized network DTO for a food pellet.
 */
@Serializable
data class PelletNetworkState(
    @SerialName("id") val id: String = "",
    @SerialName("x") val x: Float = 0f,
    @SerialName("y") val y: Float = 0f,
    @SerialName("c") val color: String = "4294967295",
    @SerialName("m") val mass: Float = 1f,
    @SerialName("e") val emoji: String = ""
)

/**
 * Compact serialized network DTO for ejected mass traveling across the arena.
 */
@Serializable
data class EjectedMassNetworkState(
    @SerialName("id") val id: String = "",
    @SerialName("o") val ownerId: String = "",
    @SerialName("x") val x: Float = 0f,
    @SerialName("y") val y: Float = 0f,
    @SerialName("vx") val vx: Float = 0f,
    @SerialName("vy") val vy: Float = 0f,
    @SerialName("c") val color: String = "4294967295",
    @SerialName("m") val mass: Float = 12f,
    @SerialName("ts") val createdAt: Double = 0.0
)

/**
 * Session result recorded upon game over or disconnect.
 */
@Serializable
data class SessionResult(
    val sessionId: String = "",
    val result: String = "", // "lost_disconnect", "lost_eaten", "quit"
    val timestamp: Double = 0.0,
    val finalMass: Float = 0f,
    val killerName: String? = null
)

/**
 * Durable user profile stored in Cloud Firestore under `users/{uid}`.
 */
@Serializable
data class UserProfile(
    val id: String = "",
    val name: String = "",
    val joinedAt: Double = 0.0,
    val gamesPlayed: Int = 0,
    val bestMass: Int = 0,
    val totalKills: Int = 0,
    val spGamesPlayed: Int = 0,
    val spBestMass: Int = 0,
    val spTotalKills: Int = 0,
    val isAnonymous: Boolean = true
)

/**
 * Abstraction for multiplayer network synchronization, auth, and leaderboards.
 */
interface MultiplayerRepository {

    // Auth
    suspend fun signInAnonymously(displayName: String): String
    suspend fun getCurrentUserId(): String?
    suspend fun signOut()
    /**
     * Permanently erase account documents for [userId] (Firestore profile + leaderboard,
     * RTDB results / world player) while still authenticated, then sign out / delete the
     * anonymous auth user. Must not sign out if the user profile delete fails.
     */
    suspend fun clearUserData(userId: String): Result<Unit>

    // Durable User Profile & High Scores (Firestore)
    suspend fun registerOrUpdateUserProfile(playerId: String, playerName: String, isAnonymous: Boolean = true)
    suspend fun recordGameJoined(playerId: String, playerName: String, isSinglePlayer: Boolean = false)
    fun observeUserProfile(playerId: String): Flow<UserProfile?>
    suspend fun recordGameStats(playerId: String, finalMass: Int, killedPlayer: Boolean = false, isSinglePlayer: Boolean = false)
    suspend fun submitScore(playerName: String, score: Int, userId: String = "")
    fun observeLeaderboard(limit: Int = 10): Flow<List<LeaderboardEntry>>

    /**
     * Create-only player report written to Realtime Database `/reports/{pushId}`.
     * [reason] is one of `offensive_name`, `cheating`, or `other`.
     */
    suspend fun submitPlayerReport(
        reporterUid: String,
        reportedUid: String,
        reportedNickname: String,
        reason: String
    ): Result<Unit>

    /**
     * Mirror a block under the signed-in user's own `/blocks/{uid}/{blockedUid}` node.
     * Local storage is the source of truth for hiding names; this write is best-effort.
     */
    suspend fun setPlayerBlocked(
        ownerUid: String,
        blockedUid: String,
        nickname: String,
        blocked: Boolean
    ): Result<Unit>

    // Ephemeral World State (Realtime Database - Single Global Room)
    fun observeWorldPlayers(): Flow<List<PlayerNetworkState>>
    fun observeWorldPellets(): Flow<List<PelletNetworkState>>
    fun observeWorldEjected(): Flow<List<EjectedMassNetworkState>>

    suspend fun broadcastPlayerState(player: GloopPlayer, forceHeartbeat: Boolean = false)
    suspend fun spawnPellet(pellet: FoodPellet)
    suspend fun spawnPellets(pellets: List<FoodPellet>)
    suspend fun removePellet(pelletId: String)
    suspend fun broadcastEjectedMass(mass: EjectedMass)
    suspend fun removeEjectedMass(massId: String)
    suspend fun consumePlayerCell(eaterPlayerId: String, eaterCellId: String, victimPlayerId: String, victimCellId: String, isLastCell: Boolean)

    // Elder & Disconnect Lifecycle
    suspend fun armDisconnectHandler(playerId: String, sessionId: String, approxMass: Float)
    suspend fun cancelDisconnectHandler(playerId: String)
    suspend fun pruneStalePlayer(stalePlayerId: String)
    suspend fun pruneStaleEjectedMasses(staleIds: List<String>)
    suspend fun drainPendingSessionResults(playerId: String): List<SessionResult>

    // Connection lifecycle
    fun goOnline()
    fun goOffline()
    suspend fun leaveWorld(playerId: String)
}
